import './MonitorFormList.scss';
import { AddAppSection } from '../add-app';
import { AsyncStateStatus } from '@sonar-software/xuder';
import { createAsyncSelectors } from '@lib/selectors';
import { isEqual } from 'lodash';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import { MonitorFormListCard } from './MonitorFormListCard';
import monitorUpsellIcon from 'images/monitor-upsell.svg';
import { SonarText } from '@ui/text';
import { Button, Card, Form, Skeleton, Space } from 'antd';
import { COPY_SPECIFIC, SETTINGS_DATA_SECURITY_COPY, URLS } from '@sonar-software/sonar-ts-constants';
import { coreActions, DataSecurityMonitorType, FeatureFlag } from '@sonar-software/sonar-core';
import { defaultConfig, getMenuItems } from '@security/monitors/monitor-form/MonitorMenu';
import { Else, If, Then, When } from 'react-if';
import Icon, { PlusOutlined } from '@ant-design/icons';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useAction, useAsyncProcessing, useCurrentUser, useFeatureFlag, useFeatureFlags, useFormList, useOnMount, usePrevious } from '@hooks';
const postMonitorSelectors = createAsyncSelectors((state) => state.core.dataSecurity.monitors.post);
const patchMonitorSelectors = createAsyncSelectors((state) => state.core.dataSecurity.monitors.byId.patch);
export const MonitorFormList = forwardRef(({ applicationId, loading, monitors, onValuesChange }, ref) => {
    const postMonitor = useAction(coreActions.requestPostCoreDataSecurityMonitors);
    const resetPostMonitor = useAction(coreActions.resetPostCoreDataSecurityMonitors);
    const patchMonitor = useAction(coreActions.requestPatchCoreDataSecurityMonitorsById);
    const resetPatchMonitor = useAction(coreActions.resetPatchCoreDataSecurityMonitorsById);
    const hasEventMonitoring = useFeatureFlag(FeatureFlag.DataSecurityDashboard);
    const { isEnabled } = useFeatureFlags();
    const [adding, setAdding] = useState(false);
    const [monitorList, setMonitorList] = useState(monitors !== null && monitors !== void 0 ? monitors : []);
    const previousMonitors = usePrevious(monitors);
    const { form } = useFormList({
        formListName: 'monitors',
        keyName: 'internalKey',
        required: ['monitorConfig.monitorTypeId', 'name']
    });
    const menuItems = useMemo(() => getMenuItems(monitorList, isEnabled, [applicationId], 'applicationRequired'), [monitorList, applicationId]);
    const { isReadOnly } = useCurrentUser();
    useOnMount(() => {
        form.validate();
    });
    useEffect(() => {
        if (monitors && !isEqual(monitors, previousMonitors)) {
            const internalMonitors = monitors.map((monitor, index) => (Object.assign(Object.assign({}, monitor), { internalKey: monitor.id || index })));
            setMonitorList(internalMonitors);
            form.setFields([
                {
                    name: ['monitors'],
                    value: internalMonitors
                }
            ]);
            form.validate();
        }
    }, [form, monitors, previousMonitors]);
    const updateMonitorList = useCallback(() => {
        const existingMonitors = form.getFieldValue('monitors');
        if (existingMonitors) {
            setMonitorList(existingMonitors);
            form.setFields([
                {
                    name: ['monitors'],
                    value: existingMonitors
                }
            ]);
        }
        form.validate();
        if (onValuesChange) {
            onValuesChange();
        }
    }, [form, onValuesChange]);
    const handleAddNew = useCallback((addCallback) => {
        const availableMenuItems = menuItems.filter((x) => !x.disabled);
        const autoSelect = availableMenuItems.length === 1 ? Object.assign({}, availableMenuItems[0].default) : Object.assign({}, defaultConfig);
        autoSelect.applicationId = applicationId;
        addCallback(autoSelect);
        setAdding(true);
    }, [applicationId, menuItems]);
    const handleValueChange = useCallback(() => {
        form.validate();
        if (onValuesChange) {
            onValuesChange();
        }
    }, [form, onValuesChange]);
    const handleCancel = useCallback((field, removeCallback) => {
        const monitor = monitorList.find((x) => x.id === form.getItem(field.name).id);
        if (adding && monitorList.length === field.name) {
            removeCallback(field.name);
            setAdding(false);
            updateMonitorList();
        }
        else if (monitor) {
            form.setFields([
                {
                    name: ['monitors', field.name],
                    value: monitor
                }
            ]);
            form.validate();
            if (onValuesChange) {
                onValuesChange();
            }
        }
    }, [adding, form, monitorList, onValuesChange, updateMonitorList]);
    const handleDelete = useCallback((field, removeCallback) => {
        removeCallback(field.name);
        updateMonitorList();
    }, [updateMonitorList]);
    const handleSave = useCallback((field) => {
        if (field.name === monitorList.length && adding) {
            setAdding(false);
        }
        updateMonitorList();
    }, [
        adding,
        monitorList.length,
        setAdding,
        updateMonitorList
    ]);
    const handleIsValid = useCallback(() => {
        if (!hasEventMonitoring) {
            return true;
        }
        const currentMonitors = form.getFieldValue(['monitors']);
        return currentMonitors.every((monitor) => form.state.find((state) => state.key === monitor.internalKey && !state.isInEdit && state.isValid));
    }, [form, hasEventMonitoring]);
    const handleOnError = useCallback(() => {
        setMonitorList(monitors);
        form.setFields([
            {
                name: ['monitors'],
                value: monitors
            }
        ]);
    }, [form, monitors]);
    const postProcess = useAsyncProcessing({
        resetAction: resetPostMonitor,
        stateSelector: postMonitorSelectors.state,
        statusHandlers: {
            [AsyncStateStatus.Received]: updateMonitorList,
            [AsyncStateStatus.Errored]: handleOnError,
            [AsyncStateStatus.Failed]: handleOnError
        }
    });
    const patchProcess = useAsyncProcessing({
        resetAction: resetPatchMonitor,
        stateSelector: patchMonitorSelectors.state,
        statusHandlers: {
            [AsyncStateStatus.Received]: updateMonitorList,
            [AsyncStateStatus.Errored]: handleOnError,
            [AsyncStateStatus.Failed]: handleOnError
        }
    });
    const saveMonitor = useCallback((monitor) => {
        var _a, _b, _c, _d, _e;
        form.validate();
        if (monitor === null || monitor === void 0 ? void 0 : monitor.id) {
            const updateMonitor = {
                id: monitor.id,
                monitorConfig: {
                    enableOnWeekends: ((_a = monitor.monitorConfig) === null || _a === void 0 ? void 0 : _a.monitorTypeId) === DataSecurityMonitorType.Uptime ?
                        (_b = monitor.monitorConfig) === null || _b === void 0 ? void 0 : _b.enableOnWeekends : undefined,
                    monitorTypeId: (_c = monitor.monitorConfig) === null || _c === void 0 ? void 0 : _c.monitorTypeId,
                    monitoringWindow: ((_d = monitor.monitorConfig) === null || _d === void 0 ? void 0 : _d.monitorTypeId) === DataSecurityMonitorType.Uptime ?
                        monitor.monitorConfig.monitoringWindow : undefined
                },
                name: monitor.name,
                notificationSchedule: monitor.notificationSchedule,
                stakeholderIds: monitor.stakeholderIds
            };
            patchProcess.watchNextCall();
            patchMonitor(updateMonitor);
        }
        else if (applicationId && ((_e = monitor.monitorConfig) === null || _e === void 0 ? void 0 : _e.monitorTypeId) && monitor.name) {
            postProcess.watchNextCall();
            postMonitor({
                applicationId: applicationId,
                monitorConfig: monitor.monitorConfig,
                monitorTypeId: monitor.monitorConfig.monitorTypeId,
                name: monitor.name,
                notificationSchedule: monitor.notificationSchedule,
                stakeholderIds: monitor.stakeholderIds
            });
            setAdding(false);
        }
    }, [applicationId, form, patchMonitor, postMonitor, patchProcess, postProcess]);
    const handleSaveAllMonitors = useCallback(() => {
        if (!hasEventMonitoring) {
            return;
        }
        form.validate();
        const allMonitors = form.getFieldValue('monitors');
        allMonitors.forEach((monitor) => {
            saveMonitor(monitor);
        });
    }, [form, hasEventMonitoring, saveMonitor]);
    useImperativeHandle(ref, () => ({
        getMonitors: () => (hasEventMonitoring ?
            form.getFieldValue('monitors')
                .map((x) => Object.fromEntries(Object.entries(x).filter(([key]) => key !== 'internalKey'))) :
            []),
        isValid: () => handleIsValid(),
        saveAllMonitors: () => handleSaveAllMonitors(),
        validate: () => form.validate()
    }));
    return (React.createElement(AddAppSection, { className: 'add-app-monitor-section', title: SETTINGS_DATA_SECURITY_COPY.MONITORS },
        React.createElement(If, { condition: hasEventMonitoring },
            React.createElement(Then, null,
                React.createElement(If, { condition: loading },
                    React.createElement(Then, null,
                        React.createElement(LoadingSpinner, { spinning: true },
                            React.createElement(Card, { title: React.createElement(Skeleton.Input, { active: true }) },
                                React.createElement(Space, { className: 'monitor-card-body', direction: 'vertical', size: 'large' },
                                    React.createElement(Skeleton.Input, { active: true }),
                                    React.createElement(Skeleton.Node, { active: true }),
                                    React.createElement(Skeleton.Input, { active: true }),
                                    React.createElement(Skeleton.Input, { active: true }))))),
                    React.createElement(Else, null,
                        React.createElement(Form, { className: 'monitor-form', form: form, layout: 'vertical', name: 'monitorsForm', onValuesChange: handleValueChange },
                            React.createElement(Form.List, { initialValue: monitorList, name: 'monitors' }, (fields, { add, remove }) => React.createElement(React.Fragment, null,
                                fields.map((field) => React.createElement("div", { key: field.key },
                                    React.createElement(Form.Item, { hidden: true, initialValue: field.key, name: [field.name, 'internalKey'], noStyle: true }),
                                    React.createElement(MonitorFormListCard, { fieldId: field.name, form: form, isNew: adding && monitorList.length === field.name, monitors: monitorList, onCancel: () => handleCancel(field, remove), onDelete: monitorList.length === field.name && adding ?
                                            undefined :
                                            () => handleDelete(field, remove), onEdit: handleValueChange, onSave: () => handleSave(field) }))),
                                React.createElement(When, { condition: !isReadOnly &&
                                        !adding &&
                                        menuItems.filter((x) => !x.disabled).length > 0 },
                                    React.createElement(Form.Item, null,
                                        React.createElement(Button, { className: 'add-monitor-button', icon: React.createElement(PlusOutlined, null), onClick: () => handleAddNew(add) }, SETTINGS_DATA_SECURITY_COPY.ADD_NEW_MONITOR))))))))),
            React.createElement(Else, null,
                React.createElement(Space, { className: 'monitor-upsell', direction: 'vertical' },
                    React.createElement(SonarText, { className: 'h2' }, COPY_SPECIFIC.PROACTIVE_APPLICATION_PROTECTION),
                    React.createElement(SonarText, null, COPY_SPECIFIC.MONITOR_UPSELL_LINE1),
                    React.createElement(SonarText, null, COPY_SPECIFIC.MONITOR_UPSELL_LINE2),
                    React.createElement(Button, { className: 'contact-us-button', href: URLS.CUSTOMER_PACKAGE_UPGRADE, rel: 'noopener', target: '_blank', type: 'primary' }, COPY_SPECIFIC.CONTACT_US),
                    React.createElement(Icon, { className: 'monitor-upsell-icon', component: () => React.createElement("img", { src: monitorUpsellIcon }) }))))));
});
